import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import DBService from '../service/DBService';
import Papa from 'papaparse';
import { saveAs } from "file-saver";
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import '../css/global.css';

function DataManager() {
    const [data, setData] = useState([]);
    const [collectionName, setCollectionName] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [deletingAll, setDeletingAll] = useState(false);
    const [deleteAllConfirmationVisible, setDeleteAllConfirmationVisible] = useState(false);
    const [overwriteConfirmationVisible, setOverwriteConfirmationVisible] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [collectionsToOverwrite, setCollectionsToOverwrite] = useState([]);
    const fileUploadRef = useRef(null);
    const toast = useRef(null);


    const categories = [
        { label: 'Logindaten', value: 'LOGIN' },
        { label: 'BOZ Organisationsmodul ', value: 'Orgamodul_BOZ' },
        { label: 'BOZ Praxismodul 1', value: 'Praxismodul1_BOZ' },
        { label: 'BOZ Praxismodul 2', value: 'Praxismodul2_BOZ' },
        { label: 'BOZ Sozialmodul', value: 'Sozialmodul_BOZ' },
        { label: 'LuL Organisationsmodul', value: 'Orgamodul_LUL' },
        { label: 'LuL Praxismodel 1', value: 'Praxismodul1_LUL' },
        { label: 'LuL Praxismodul 2', value: 'Praxismodul2_LUL' },
        { label: 'LuL Sozialmodul', value: 'Sozialmodul_LUL' }
    ];

    const handleFileUpload = async (event) => {
        const file = event.files[0];
        if (file) {
            setUploading(true);
            setUploadMessage('Hochladen läuft...');
            try {
                await DBService.uploadCsv(file, collectionName);
                setUploadMessage('CSV erfolgreich hochgeladen!');
                toast.current.show({ severity: 'success', summary: 'Erfolg', detail: 'CSV erfolgreich hochgeladen!', life: 3000 });
                loadData();
            } catch (error) {
                setUploadMessage('Fehler beim Hochladen der CSV-Datei.');
                toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Hochladen der CSV-Datei.', life: 3000 });
                console.error(error);
            } finally {
                setUploading(false);
                fileUploadRef.current.clear();
            }
        }
    };

    const loadData = async () => {
        if (!collectionName) {
            toast.current.show({ severity: 'warn', summary: 'Keine Auswahl', detail: 'Bitte wählen Sie eine Collection aus.', life: 3000 });
            return;
        }

        setLoading(true);
        try {
            const fetchedData = await DBService.fetchData(collectionName);
            const cleanedData = fetchedData.map(({ id, ...rest }) => rest);
            setData(cleanedData);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Laden der Daten.', life: 3000 });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCsv = async () => {
        if (!collectionName) {
            toast.current.show({ severity: 'warn', summary: 'Keine Auswahl', detail: 'Bitte wählen Sie eine Collection aus.', life: 3000 });
            return;
        }

        setDownloading(true);
        try {
            // Fetch latest data from DB to include autosave changes
            const fetchedData = await DBService.fetchData(collectionName);
            if (!fetchedData || fetchedData.length === 0) {
                toast.current.show({ severity: 'warn', summary: 'Keine Daten', detail: 'Keine Daten zum Herunterladen verfügbar.', life: 3000 });
                return;
            }

            const csvData = fetchedData.map(({ id, ...rest }) => rest);
            const csv = Papa.unparse(csvData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `${collectionName}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.current.show({ severity: 'success', summary: 'Download', detail: 'CSV erfolgreich heruntergeladen.', life: 3000 });
        } catch (error) {
            console.error('Fehler beim Herunterladen der CSV:', error);
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Herunterladen der CSV.', life: 3000 });
        } finally {
            setDownloading(false);
        }
    };

    const backupAndDeleteCollection = async () => {
        try {
            // Ensure we back up the latest data from DB (in case autosave updated it)
            const fetchedData = await DBService.fetchData(collectionName);
            const csvData = Papa.unparse(fetchedData.map(({ id, ...rest }) => rest));
            const currentDate = new Date().toISOString().split('T')[0];
            const fileName = `${currentDate}_${collectionName}.csv`;
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            await DBService.clearCollection(collectionName); // Funktion zum Leeren der Collection
            setData([]);
            setCollectionName(null);
            toast.current.show({ severity: 'success', summary: 'Gelöscht', detail: 'Collection erfolgreich gesichert und gelöscht.', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Löschen der Collection.', life: 3000 });
            console.error(error);
        } finally {
            setDeleteConfirmationVisible(false);
        }
    };

    const showDeleteConfirmation = () => {
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
    };

    const handleCollectionChange = (e) => {
        setCollectionName(e.value);
        setData([]);
        fileUploadRef.current.clear();
    };

    const resetView = () => {
        setCollectionName(null);
        setData([]);
        fileUploadRef.current.clear();
        toast.current.show({ severity: 'info', summary: 'Zurückgesetzt', detail: 'Ansicht wurde zurückgesetzt.', life: 3000 });
    };

    const downloadAllCollections = async () => {
        setDownloading(true);
        try {
            const zipBlob = await DBService.downloadAllCollections(categories);
            saveAs(zipBlob, "firebase_data.zip");
            toast.current.show({ severity: 'success', summary: 'Download', detail: 'ZIP erfolgreich heruntergeladen.', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Herunterladen der Collections.', life: 3000 });
        } finally {
            setDownloading(false);
        }
    };

    const backupAndDeleteAllCollections = async () => {
        setDeletingAll(true);
        try {
            // Backup aller Collections als ZIP
            const zipBlob = await DBService.downloadAllCollections(categories);
            saveAs(zipBlob, "firebase_backup.zip");
            toast.current.show({ severity: 'success', summary: 'Backup', detail: 'Backup erfolgreich erstellt.', life: 3000 });

            // Löschen aller Collections
            await DBService.clearAllCollections();
            setData([]);
            setCollectionName(null);
            toast.current.show({ severity: 'success', summary: 'Datenbank geleert', detail: 'Alle Collections wurden gesichert und gelöscht.', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Löschen der Collections.', life: 3000 });
            console.error(error);
        } finally {
            setDeletingAll(false);
            setDeleteAllConfirmationVisible(false);
        }
    };

    const showDeleteAllConfirmation = () => {
        setDeleteAllConfirmationVisible(true);
    };

    const hideDeleteAllConfirmation = () => {
        setDeleteAllConfirmationVisible(false);
    };

    const handleMultipleFileUpload = async (event) => {
        const files = event.files;
        if (!files || files.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'Keine Datei', detail: 'Bitte wählen Sie CSV-Dateien aus.', life: 3000 });
            return;
        }

        const existingCollections = [];
        const filesToProcess = [];

        for (const file of files) {
            const fileNameWithoutExtension = file.name.replace('.csv', '').trim();
            const matchingCategory = categories.find(category => category.value === fileNameWithoutExtension);

            if (matchingCategory) {
                const fetchedData = await DBService.fetchData(matchingCategory.value);
                if (fetchedData.length > 0) {
                    existingCollections.push(matchingCategory.value);
                }
                filesToProcess.push({ file, collectionName: matchingCategory.value });
            } else {
                toast.current.show({ severity: 'error', summary: 'Fehlgeschlagen', detail: `Datei "${file.name}" hat keine passende Collection.`, life: 4000 });
            }
        }

        if (existingCollections.length > 0) {
            setFilesToUpload(filesToProcess);
            setCollectionsToOverwrite(existingCollections);
            setOverwriteConfirmationVisible(true);
        } else {
            await uploadFiles(filesToProcess);
        }
    };

    const uploadFiles = async (files) => {
        setUploading(true);
        setUploadMessage('CSV-Dateien werden hochgeladen...');

        try {
            const uploadPromises = files.map(async ({ file, collectionName }) => {
                await DBService.uploadCsv(file, collectionName);
                return { file: file.name, status: 'Erfolgreich' };
            });

            const results = await Promise.all(uploadPromises);

            results.forEach(result => {
                toast.current.show({
                    severity: result.status === 'Erfolgreich' ? 'success' : 'error',
                    summary: result.status,
                    detail: `Datei: ${result.file}`,
                    life: 4000
                });
            });

            setUploadMessage('CSV-Upload abgeschlossen!');
            loadData(); // Daten nach Upload neu laden
        } catch (error) {
            setUploadMessage('Fehler beim Hochladen der CSV-Dateien.');
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Hochladen der Dateien.', life: 3000 });
            console.error(error);
        } finally {
            setUploading(false);
            fileUploadRef.current.clear();
        }
    };

    const backupAndOverwriteCollections = async () => {
        setUploading(true);
        setOverwriteConfirmationVisible(false);
        setUploadMessage('Backup und Überschreiben der Collections läuft...');

        try {
            for (const collectionName of collectionsToOverwrite) {
                const fetchedData = await DBService.fetchData(collectionName);
                if (fetchedData.length > 0) {
                    const csvData = Papa.unparse(fetchedData);
                    const fileName = `${new Date().toISOString().split('T')[0]}_${collectionName}.csv`;
                    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                    saveAs(blob, fileName);

                    await DBService.clearCollection(collectionName);
                }
            }

            await uploadFiles(filesToUpload);
            toast.current.show({ severity: 'success', summary: 'Überschrieben', detail: 'Collections wurden gesichert und neu hochgeladen.', life: 4000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Fehler', detail: 'Fehler beim Überschreiben der Collections.', life: 4000 });
            console.error(error);
        } finally {
            setUploading(false);
            setFilesToUpload([]);
            setCollectionsToOverwrite([]);
        }
    };

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog visible={deleteConfirmationVisible} onHide={hideDeleteConfirmation}
                message="Sind Sie sicher, dass Sie die Collection löschen möchten? Es wird ein Backup erstellt und heruntergeladen."
                header="Löschen bestätigen"
                icon="pi pi-exclamation-triangle"
                accept={backupAndDeleteCollection}
                reject={hideDeleteConfirmation}
            />
            <center><h1>CSV Datenmanagement</h1></center>
            <div className="p-grid">
                <div className="p-col-12">
                    <Card title="Collection Auswahl und Vorschau">
                        <Dropdown
                            value={collectionName}
                            options={categories}
                            onChange={handleCollectionChange}
                            placeholder="Collection auswählen"
                            style={{ width: '350px', margin: '1em' }}
                        />
                        <Button
                            label="Daten laden"
                            icon="pi pi-database"
                            onClick={loadData}
                            className="p-button-success p-mt-2"
                            style={{ maxWidth: '250px', margin: '1em' }}
                        />
                        <Button
                            label="CSV herunterladen"
                            icon="pi pi-download"
                            onClick={handleDownloadCsv}
                            className="p-button-info p-mt-2"
                            style={{ maxWidth: '250px', margin: '1em' }}
                        />
                        <Button
                            label="Collection löschen"
                            icon="pi pi-trash"
                            onClick={showDeleteConfirmation}
                            className="p-button-danger p-mt-2"
                            style={{ maxWidth: '250px', margin: '1em' }}
                        />
                        <Button
                            label="Ansicht zurücksetzen"
                            icon="pi pi-refresh"
                            onClick={resetView}
                            className="p-button-warning p-mt-2"
                            style={{ maxWidth: '250px', margin: '1em' }}
                        />
                        {loading ? (
                            <div>
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="p-mb-2">
                                        <Skeleton width="100%" height="2em" className="mb-2" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <DataTable value={data} style={{ marginTop: '1em' }}>
                                {data.length > 0 && Object.keys(data[0]).map((key, index) => (
                                    key !== 'id' && <Column key={index} field={key} header={key} />
                                ))}
                            </DataTable>
                        )}
                    </Card>
                </div>
            </div>
            <div className="p-grid">
                <div className="p-col-6">
                    <Card title="CSV Upload">
                        <p>Beim Hochladen mehrerer Dateien bitte beachten, dass die Namenskonventionen der CSV Dateien eingehalten wird. </p>
                        <ConfirmDialog
                            visible={overwriteConfirmationVisible}
                            onHide={() => setOverwriteConfirmationVisible(false)}
                            message={`Es gibt bereits Daten in folgenden Collections: ${collectionsToOverwrite.join(', ')}. 
        Möchten Sie diese überschreiben? Ein Backup wird vorher erstellt.`}
                            header="Bestehende Daten überschreiben?"
                            icon="pi pi-exclamation-triangle"
                            accept={backupAndOverwriteCollections}
                            reject={() => setOverwriteConfirmationVisible(false)}
                        />
                        <FileUpload
                            ref={fileUploadRef}
                            name="csv"
                            accept=".csv"
                            customUpload
                            multiple
                            auto={false}
                            uploadHandler={handleMultipleFileUpload}
                            chooseLabel="Dateien auswählen"
                            uploadLabel={uploading ? "Wird hochgeladen..." : "Hochladen"}
                            disabled={uploading}
                            className="p-mb-3"
                        />
                        <p style={{ marginTop: '1em', color: uploading ? 'blue' : 'green' }}>
                            {uploadMessage}
                        </p>
                    </Card>
                </div>
                <div className="p-col-6">
                    <Card title="Gesamte Collection herunterladen">
                        <Button
                            label={downloading ? "Lädt..." : "Alle Collections herunterladen"}
                            icon={downloading ? "pi pi-spin pi-spinner" : "pi pi-download"}
                            onClick={downloadAllCollections}
                            className="p-button-success p-mt-2"
                            disabled={downloading}
                            style={{ maxWidth: '100%', margin: '1em' }}
                        />
                        <ConfirmDialog
                            visible={deleteAllConfirmationVisible}
                            onHide={hideDeleteAllConfirmation}
                            message="Sind Sie sicher, dass Sie ALLE Collections löschen möchten? Es wird ein Backup erstellt und dann die gesamte Datenbank geleert."
                            header="Alle Collections löschen bestätigen"
                            icon="pi pi-exclamation-triangle"
                            accept={backupAndDeleteAllCollections}
                            reject={hideDeleteAllConfirmation}
                        />
                        <Button
                            label={deletingAll ? "Löscht..." : "Alle Collections löschen"}
                            icon={deletingAll ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                            onClick={showDeleteAllConfirmation}
                            className="p-button-danger p-mt-2"
                            disabled={deletingAll}
                            style={{ maxWidth: '100%', margin: '1em' }}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
}

export default DataManager;